import React, { useState } from "react";
import { SettingsCard } from "../SettingsCard";
import { Toggle } from "../../../components/ui/Toggle";

// ── Feature rows config ───────────────────────────────────────────────────────

const AI_FEATURES = [
    {
        key:         "auto_diagnosis",
        label:       "Auto Diagnosis Suggestions",
        sub:         "AI-powered diagnosis recommendations",
        rowBg:       "bg-gradient-to-r from-purple-50 to-pink-50",
        iconBg:      "from-purple-500 to-pink-500",
        icon: (
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
        ),
        enabled: true,
    },
    {
        key:         "smart_template",
        label:       "Smart Template Filling",
        sub:         "Auto-complete forms based on history",
        rowBg:       "bg-gradient-to-r from-blue-50 to-cyan-50",
        iconBg:      "from-blue-500 to-cyan-400",
        icon: (
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
            </svg>
        ),
        enabled: true,
    },
    {
        key:         "auto_reports",
        label:       "Auto Report Drafts",
        sub:         "Generate preliminary reports automatically",
        rowBg:       "bg-gradient-to-r from-green-50 to-emerald-50",
        iconBg:      "from-green-500 to-emerald-400",
        icon: (
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
            </svg>
        ),
        enabled: false,
    },
] as const;

// ── Stat boxes config ─────────────────────────────────────────────────────────

const STATS = [
    { value: "2,847", label: "Forms Auto-filled",   valueCls: "text-blue-500",   boxBg: "bg-white border border-gray-100"          },
    { value: "156",   label: "Diagnoses Suggested",  valueCls: "text-purple-500", boxBg: "bg-gradient-to-br from-pink-50 to-purple-50" },
    { value: "42h",   label: "Time Saved",            valueCls: "text-green-500",  boxBg: "bg-gradient-to-br from-green-50 to-emerald-50" },
];

// ── Component ─────────────────────────────────────────────────────────────────

export const SmartAutomation: React.FC = () => {
    const [features, setFeatures] = useState(
        Object.fromEntries(AI_FEATURES.map(f => [f.key, f.enabled])) as Record<string, boolean>
    );

    const toggle = (key: string) =>
        setFeatures(prev => ({ ...prev, [key]: !prev[key] }));

    return (
        <div className="space-y-5">

            {/* ── Section badge row ─────────────────────────────────────── */}
            <div className="flex items-center gap-3">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 px-3 py-1 text-xs font-semibold text-white shadow-sm">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
                        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                    </svg>
                    AI Powered
                </span>
            </div>

            {/* ── Card 1: AI Preferences ────────────────────────────────── */}
            <SettingsCard
                title="AI Preferences"
                subtitle="Enable intelligent automation features"
                icon={
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                    </svg>
                }
            >
                <div className="space-y-2">
                    {AI_FEATURES.map(f => (
                        <div key={f.key}
                            className={`flex items-center gap-4 rounded-xl px-4 py-3.5 ${f.rowBg}`}>

                            {/* Icon */}
                            <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${f.iconBg} flex items-center justify-center flex-shrink-0`}>
                                {f.icon}
                            </div>

                            {/* Text */}
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900">{f.label}</p>
                                <p className="text-xs text-gray-500 mt-0.5">{f.sub}</p>
                            </div>

                            {/* Toggle */}
                            <Toggle
                                enabled={features[f.key]}
                                onToggle={() => toggle(f.key)}
                            />
                        </div>
                    ))}

                    {/* Beta warning */}
                    <div className="flex items-center gap-2 rounded-xl bg-amber-50 border border-amber-100 px-4 py-3 mt-1">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                            stroke="#f59e0b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                            className="flex-shrink-0">
                            <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                        </svg>
                        <p className="text-xs text-amber-600">
                            AI features are in beta. Please review all AI-generated content before finalizing.
                        </p>
                    </div>
                </div>
            </SettingsCard>

            {/* ── Card 2: Automation Analytics ──────────────────────────── */}
            <SettingsCard
                title="Automation Analytics"
                icon={
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="20" x2="18" y2="10"/>
                        <line x1="12" y1="20" x2="12" y2="4"/>
                        <line x1="6"  y1="20" x2="6"  y2="14"/>
                    </svg>
                }
            >
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {STATS.map(({ value, label, valueCls, boxBg }) => (
                        <div key={label}
                            className={`rounded-2xl p-5 flex flex-col items-center justify-center text-center ${boxBg}`}>
                            <p className={`text-3xl font-bold ${valueCls}`}>{value}</p>
                            <p className="text-xs text-gray-500 mt-1.5">{label}</p>
                        </div>
                    ))}
                </div>
            </SettingsCard>

        </div>
    );
};
